<template>
    <modal-jetstream :show="show" :max-width="'2xl'">
        <div class="rounded-md overflow-x-auto m-2 mb-4">
            <div class="px-4 pt-4">
                <h3 class="text-lg font-semibold text-gray-900">Mark as Paid - <span>{{ chargeTitle }}</span></h3>
            </div>
            <form @submit.prevent="storeManualPayment" class="space-y-4 p-4">
                <div class="space-y-2 divide-y divide-gray-200">
                    <div class="mt-6 grid grid-cols-3 gap-y-4 gap-x-4 sm:grid-cols-6">
                        <!-- amount -->
                        <div class="col-span-full">
                            <label for="manual_otc_amount" :class="form.errors.amount ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">
                                Amount <span class="text-red-600">*</span>
                            </label>
                            <div class="mt-1 relative rounded-md">
                                <div class="absolute left-0 pl-3 mt-2 pointer-events-none inset-y-0">
                                    <font-awesome-icon :icon="faDollar" class="text-gray-400" />
                                </div>
                                <input
                                    type="number"
                                    step="0.01"
                                    v-model="form.amount"
                                    id="manual_otc_amount"
                                    :class="[form.errors.amount ? 'border-red-300' : 'border-gray-300']"
                                    class="w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block sm:text-sm border p-2 rounded-md pl-8">
                                <p v-text="form.errors.amount" class="text-red-500 text-sm"></p>
                            </div>
                            <p class="text-xs text-gray-500 mt-1">Remaining: {{ formatCurrency(remaining) }}</p>
                        </div>

                        <!-- paid_date -->
                        <div class="col-span-full">
                            <label for="otc_paid_date" :class="form.errors.paid_date ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">
                                Payment Date <span class="text-red-600">*</span>
                            </label>
                            <input type="date" v-model="form.paid_date" id="otc_paid_date"
                                   :class="[form.errors.paid_date ? 'border-red-300' : 'border-gray-300']"
                                   class="w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block sm:text-sm border p-2 rounded-md" />
                            <p v-text="form.errors.paid_date" class="text-red-500 text-sm"></p>
                        </div>

                        <!-- description -->
                        <div class="col-span-full">
                            <label for="otc_description" :class="form.errors.description ? 'text-red-500' : 'text-gray-700'" class="block text-sm font-medium">
                                Description (notes)
                            </label>
                            <textarea id="otc_description" v-model="form.description"
                                      :class="[form.errors.description ? 'border-red-300' : 'border-gray-300']"
                                      class="w-full shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block sm:text-sm border p-2 rounded-md"
                                      rows="3"></textarea>
                            <p v-text="form.errors.description" class="text-red-500 text-sm"></p>
                        </div>
                    </div>
                </div>

                <!-- Submit Button -->
                <div class="text-right pr-2 flex justify-end space-x-2 mb-2">
                    <button @click="$emit('close')"
                            type="button"
                            class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 h-10">
                        Close
                    </button>
                    <form-button
                        :form="form"
                        type="submit"
                        class="h-10 px-4 text-sm"
                        :disabled="!one_time_charge || remaining <= 0">
                        Save
                    </form-button>
                </div>
            </form>
        </div>
    </modal-jetstream>
</template>

<script setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faDollar } from '@fortawesome/pro-light-svg-icons'

import ModalJetstream from "@/Jetstream/Modal.vue";
import FormButton from '@/Shared/FormButton.vue';
import { ref, watch, computed, getCurrentInstance } from 'vue';

const props = defineProps({
    show: Boolean,
    rented_property_id: [Number, String],
    one_time_charge: {
        type: Object,
        default: null,
    },
});
const chargeTitle = computed(() => props.one_time_charge?.title || 'One Time Charge');
const remaining = computed(() => {
    if (!props.one_time_charge) return 0;
    const r = props.one_time_charge.remaining;
    if (typeof r === 'number') return r;
    return parseFloat(String(r).replace(/,/g, '')) || 0;
});
const defaultAmount = computed(() => remaining.value);

const emit = defineEmits(['close', 'saved']);

const appInstance = getCurrentInstance();
const inertia = appInstance && appInstance.proxy ? appInstance.proxy.$inertia : null;

const form = ref({
    rented_property_id: props.rented_property_id,
    rented_property_charge_id: props.one_time_charge?.id,
    amount: defaultAmount.value,
    paid_date: new Date().toISOString().slice(0, 10),
    description: null,
    errors: {},
});

watch(() => props.show, (val) => {
    if (val && props.one_time_charge) {
        form.value.rented_property_id = props.rented_property_id;
        form.value.rented_property_charge_id = props.one_time_charge.id;
        form.value.amount = defaultAmount.value;
        form.value.description = null;
        form.value.paid_date = new Date().toISOString().slice(0, 10);
        form.value.errors = {};
    }
});
watch(() => props.rented_property_id, () => { form.value.rented_property_id = props.rented_property_id; });
watch(() => props.one_time_charge, (charge) => {
    if (charge) {
        form.value.rented_property_charge_id = charge.id;
        form.value.amount = typeof charge.remaining === 'number'
            ? charge.remaining
            : parseFloat(String(charge.remaining).replace(/,/g, '')) || 0;
    }
}, { deep: true });

function formatCurrency(amount) {
    if (amount == null) amount = 0;
    amount = parseFloat(amount);
    return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function storeManualPayment() {
    if (!form.value.amount || parseFloat(form.value.amount) <= 0) {
        form.value.errors = { amount: 'Amount must be greater than 0.' };
        return;
    }
    if (parseFloat(form.value.amount) > remaining.value) {
        form.value.errors = { amount: 'Amount cannot be greater than remaining.' };
        return;
    }

    if (!inertia) return;
    inertia.post(route('rent-payments.store-manual-one-time-charge-payment'), {
        rented_property_id: form.value.rented_property_id,
        rented_property_charge_id: form.value.rented_property_charge_id,
        amount: form.value.amount,
        paid_date: form.value.paid_date,
        description: form.value.description,
    }, {
        preserveState: false,
        onSuccess: () => {
            emit('saved');
            emit('close');
            form.value.description = null;
            form.value.errors = {};
        },
        onError: (errors) => {
            form.value.errors = errors;
        }
    });
}
</script>
